import React, { useState, useCallback, useMemo, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import Page from "components/Page";
import Card from "components/Card";
import Input from "components/Input";
import Text from "components/Text";
import Loader from "components/Loader";
import constants from "../../../../../constants";
import {
  addPolicyFolder,
  editPolicyFolder,
  getPolicyFolderById,
} from "repositories/office-policy-management-repository";
import { useQueryClient, useQuery } from "react-query";
import { decodeId } from "utils";
import toast from "react-hot-toast";
import useManageOfficePolicyController from "../../hooks/useManageOfficePolicyController";
import SelectCategoryModal from "../../Modals/SelectCategoryModal";
import SelectOfficesModal from "../../Modals/SelectOfficesModal";
import "../../OfficePolicyManagement.scss";
import styles from "../../OfficePolicyManagement.module.scss";
import InviteesPopup from "accountOwner/pages/EventDetails/components/Modals/InviteesPopup";

const TITLE_MAX_LENGTH = 200;
const OFFICE_DISPLAY_COUNT = 3;

function mapFolderOfficesToForm(offices) {
  if (!Array.isArray(offices) || offices.length === 0) return [];
  return offices
    .map((o) => ({
      id: o.officeId,
      name: o.officeName ?? "",
      officeName: o.officeName ?? "",
    }))
    .filter((o) => o.id != null);
}

const AddEditPolicyFolder = ({ t }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { officePolicyId: officePolicyIdParam } = useParams();
  const officePolicyIdDecoded = decodeId(officePolicyIdParam);
  const isEditMode = Boolean(officePolicyIdDecoded);

  const [title, setTitle] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedOffices, setSelectedOffices] = useState({});
  const [selectedOfficeList, setSelectedOfficeList] = useState([]);
  const [isAllOffice, setIsAllOffice] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [officesModalOpen, setOfficesModalOpen] = useState(false);
  const [officeListPopupOpen, setOfficeListPopupOpen] = useState(false);
  const [invitePopupPosition, setInvitePopupPosition] = useState({
    top: 0,
    left: 0,
  });
  const [formPrefilled, setFormPrefilled] = useState(false);

  const {
    data: { categoryList, refetchCategories, accountOwnerId },
  } = useManageOfficePolicyController(t, history, false, undefined, () => {}, {
    isCategoryModalOpen: categoryModalOpen,
  });

  const {
    data: folderData,
    isLoading: folderLoading,
    error: folderError,
  } = useQuery(
    ["policyFolder", officePolicyIdDecoded],
    () => getPolicyFolderById(officePolicyIdDecoded),
    { enabled: isEditMode && !!officePolicyIdDecoded },
  );

  useEffect(() => {
    if (!isEditMode || !folderData || formPrefilled) return;
    const folder = folderData?.data ?? folderData;

    setTitle(folder?.title ?? "");
    setIsAllOffice(!!folder?.isAllOffice);

    if (folder?.policyCategory) {
      const cat = folder.policyCategory;
      setSelectedCategory({ id: cat.id, title: cat.title ?? "" });
    } else if (folder?.policyCategoryId && Array.isArray(categoryList)) {
      const category = categoryList.find(
        (c) => (c.id ?? c.Id) === folder.policyCategoryId,
      );
      if (category) setSelectedCategory(category);
    }

    const officeList = mapFolderOfficesToForm(folder?.offices ?? []);
    if (officeList.length > 0) {
      setSelectedOfficeList(officeList);
      setSelectedOffices(
        officeList.reduce((acc, o) => ({ ...acc, [o.id]: true }), {}),
      );
    }
    setFormPrefilled(true);
  }, [isEditMode, folderData, categoryList, formPrefilled]);

  const onBack = useCallback(() => {
    history.push(
      constants.routes.officePolicyManagements.officePolicyManagement,
    );
  }, [history]);

  const validate = () => {
    const next = {};
    const trimmedTitle = (title || "").trim();
    if (!trimmedTitle) {
      next.title = t("officePolicyManagement.titleRequired");
    } else if (trimmedTitle.length > TITLE_MAX_LENGTH) {
      next.title = t("officePolicyManagement.titleMaxLength", {
        max: TITLE_MAX_LENGTH,
      });
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setErrors({});

    const officeIds = selectedOfficeList
      .map((o) => o?.id ?? o?.officeId)
      .filter((id) => id != null);

    const commonParams = {
      Title: title.trim(),
      PolicyCategoryId: selectedCategory?.id ?? null,
      OwnerId: accountOwnerId ?? null,
      IsAllOffice: isAllOffice,
      OfficeIds: officeIds,
    };

    try {
      if (isEditMode) {
        await editPolicyFolder({ ...commonParams, Id: folderData?.id });
        queryClient.invalidateQueries(["/officePolicies/policyFolderList"]);
        queryClient.invalidateQueries(["policyFolder", officePolicyIdDecoded]);
      } else {
        await addPolicyFolder(commonParams);
      }
      queryClient.invalidateQueries(["/PolicyCategory", accountOwnerId]);
      queryClient.invalidateQueries(["/Office/AllActive"]);
      queryClient.invalidateQueries(["/officePolicies/accountowner"]);
      toast.success(
        isEditMode
          ? t("officePolicyManagement.policyFolderUpdated") ||
              "Policy folder updated"
          : t("officePolicyManagement.policyFolderCreated"),
      );
      onBack();
    } catch (err) {
      const message = err?.message || "";
      const isDuplicate =
        message.toLowerCase().includes("same name") ||
        message.toLowerCase().includes("already exists") ||
        message.toLowerCase().includes("duplicate");
      setErrors({
        title: isDuplicate
          ? t("officePolicyManagement.policyWithSameNameAlreadyExists")
          : message,
      });
      if (!isDuplicate) toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryApply = (category) => {
    setSelectedCategory(category);
    setCategoryModalOpen(false);
    if (category) {
      setErrors((prev) => ({ ...prev, category: undefined }));
    }
  };

  const handleOfficesApply = (selectedIds, selectedList, allOfficeSelected) => {
    setSelectedOffices(selectedIds ?? selectedOffices);
    setSelectedOfficeList(Array.isArray(selectedList) ? selectedList : []);
    setIsAllOffice(!!allOfficeSelected);
    setOfficesModalOpen(false);
    if (
      allOfficeSelected ||
      (Array.isArray(selectedList) && selectedList.length > 0)
    ) {
      setErrors((prev) => ({ ...prev, office: undefined }));
    }
  };

  const handlePopupToggle = useCallback((event, setter) => {
    event.stopPropagation();
    setInvitePopupPosition({ top: 40, left: 40 });
    setter((prev) => !prev);
  }, []);

  const initialOfficeIdsForModal = useMemo(
    () =>
      selectedOfficeList.reduce((acc, o) => {
        const id = o.id ?? o.officeId;
        if (id != null) acc[id] = true;
        return acc;
      }, {}),
    [selectedOfficeList],
  );

  const officeDisplayRender = useMemo(() => {
    if (selectedOfficeList.length === 0)
      return t("officePolicyManagement.noOfficeSelected");
    const names = selectedOfficeList.map((o) => o.name ?? o.officeName ?? "");
    if (names.length <= OFFICE_DISPLAY_COUNT) return names.join(", ");
    const firstThree = names.slice(0, OFFICE_DISPLAY_COUNT).join(", ");
    const moreCount = names.length - OFFICE_DISPLAY_COUNT;
    return (
      <span>
        {firstThree},{" "}
        <button
          type="button"
          className="link-btn p-0 border-0 bg-transparent"
          onClick={(e) => handlePopupToggle(e, setOfficeListPopupOpen)}
        >
          +{moreCount} {t("officePolicyManagement.more")}
        </button>
      </span>
    );
  }, [selectedOfficeList, t]);

  const categoryDisplayText = selectedCategory?.title ?? "";

  return (
    <Page
      onBack={onBack}
      title={
        isEditMode
          ? t("officePolicyManagement.editPolicy")
          : t("officePolicyManagement.addNewPolicy")
      }
    >
      {saving && <Loader />}

      <form onSubmit={handleSubmit} className="w-100 form-wrapper p-0">
        <Card className={styles.add_new_note_card_wrapper}>
          <div className={styles.add_new_note_parent_wrapper}>
            <Input
              Title={t("title")}
              Type="text"
              Placeholder={t("officePolicyManagement.enterTitle")}
              Name="title"
              Value={title}
              HandleChange={(e) => {
                setTitle(e.target.value);
                setErrors((prev) => ({ ...prev, title: undefined }));
              }}
              Error={errors.title}
              MaxLength={String(TITLE_MAX_LENGTH)}
            />

            <div className={styles.assiness_input}>
              <div className="d-flex justify-content-between align-items-center">
                <Text size="13px" weight="400" color="#79869A">
                  {t("officePolicyManagement.category")}
                </Text>
                <button
                  type="button"
                  className="link-btn m-0 p-0 border-0 bg-transparent"
                  onClick={() => {
                    setCategoryModalOpen(true);
                    setErrors((prev) => ({ ...prev, category: undefined }));
                  }}
                >
                  {t("officePolicyManagement.select")}
                </button>
              </div>
              <Text size="14px" weight="600" color="#102C42" marginTop="8px">
                {categoryDisplayText ||
                  t("officePolicyManagement.noCategorySelected")}
              </Text>
              {errors.category && (
                <Text
                  size="12px"
                  weight="400"
                  color="#EE4F4F"
                  marginTop="4px"
                  role="alert"
                >
                  {errors.category}
                </Text>
              )}
            </div>

            <div className={`${styles.assiness_input} position-relative`}>
              <div className="d-flex justify-content-between align-items-center">
                <Text size="13px" weight="400" color="#79869A">
                  {t("officePolicyManagement.office")}
                </Text>
                <button
                  type="button"
                  className="link-btn m-0 p-0 border-0 bg-transparent"
                  onClick={() => {
                    setSelectedOffices(initialOfficeIdsForModal);
                    setOfficesModalOpen(true);
                    setErrors((prev) => ({ ...prev, office: undefined }));
                  }}
                >
                  {t("officePolicyManagement.select")}
                </button>
              </div>
              <Text size="14px" weight="600" color="#102C42" marginTop="8px">
                {officeDisplayRender}
              </Text>
              {errors.office && (
                <Text
                  size="12px"
                  weight="400"
                  color="#EE4F4F"
                  marginTop="4px"
                  role="alert"
                >
                  {errors.office}
                </Text>
              )}
              {officeListPopupOpen && selectedOfficeList.length > 0 && (
                <InviteesPopup
                  position={invitePopupPosition}
                  handleInvitePopupToggle={(e) =>
                    handlePopupToggle(e, setOfficeListPopupOpen)
                  }
                  list={selectedOfficeList}
                  onlyOfffice={true}
                  isForEvent={true}
                />
              )}
            </div>

            <div className={styles.btn_wrapper}>
              <button
                type="submit"
                className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
                title="save"
                disabled={saving}
              >
                {isEditMode
                  ? t("officePolicyManagement.updatePolicy") || "Update"
                  : t("officePolicyManagement.addPolicy")}
              </button>
              <button
                type="button"
                className="mb-md-3 button button-round button-border btn-mobile-link button-dark"
                title="cancel"
                onClick={onBack}
              >
                {t("officePolicyManagement.cancel")}
              </button>
            </div>
          </div>
        </Card>
      </form>

      {categoryModalOpen && (
        <SelectCategoryModal
          isOpen={categoryModalOpen}
          closeModal={() => setCategoryModalOpen(false)}
          categories={categoryList}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
          onApply={handleCategoryApply}
          accountOwnerId={accountOwnerId}
          refetchCategories={refetchCategories}
        />
      )}

      {officesModalOpen && (
        <SelectOfficesModal
          isOpen={officesModalOpen}
          closeModal={() => setOfficesModalOpen(false)}
          ownerId={accountOwnerId}
          selectedOfficeIds={selectedOffices}
          onSelectionChange={setSelectedOffices}
          onApply={handleOfficesApply}
        />
      )}
    </Page>
  );
};

export default withTranslation()(AddEditPolicyFolder);
